import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { AdminPortal, AdminProject, PortalProjectLink } from '../../types/admin';
import { adminProjectApi, portalsApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';

interface PortalProjectsDialogProps {
  open: boolean;
  portal: AdminPortal | null;
  onClose: () => void;
}

/** Hộp thoại gán dự án vào cổng truy cập — checklist toàn bộ dự án. */
export function PortalProjectsDialog({ open, portal, onClose }: PortalProjectsDialogProps): JSX.Element {
  const [allProjects, setAllProjects] = useState<AdminProject[]>([]);
  const [assigned, setAssigned] = useState<PortalProjectLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!portal) return;
    setLoading(true);
    try {
      const [projectPage, links] = await Promise.all([
        adminProjectApi.list({ limit: 200 }),
        portalsApi.listProjects(portal.id),
      ]);
      setAllProjects(projectPage.items);
      setAssigned(links);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách dự án'), 'error');
    } finally {
      setLoading(false);
    }
  }, [portal]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const assignedIds = useMemo(() => new Set(assigned.map((p) => p.id)), [assigned]);

  const toggle = async (project: AdminProject) => {
    if (!portal) return;
    setPendingId(project.id);
    try {
      if (assignedIds.has(project.id)) {
        await portalsApi.removeProject(portal.id, project.id);
      } else {
        await portalsApi.assignProject(portal.id, project.id);
      }
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật dự án của cổng'), 'error');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dự án thuộc cổng: {portal?.display_name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Chỉ các dự án được tick mới hiện ra khi truy cập bằng domain này.
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <FormGroup>
            {allProjects.map((project) => (
              <FormControlLabel
                key={project.id}
                control={
                  <Checkbox
                    checked={assignedIds.has(project.id)}
                    onChange={() => void toggle(project)}
                    disabled={pendingId === project.id}
                    size="small"
                  />
                }
                label={project.name}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Xong
        </Button>
      </DialogActions>
    </Dialog>
  );
}
